import { DateTime } from 'luxon';
import { APIConfig } from 'src/config';
import {
  TIME_ZONE_SYSTEM,
} from 'src/util/constants';

/**
 * Transform a date implemented as a DateTime in local time, to
 * a timestamp in DateTime in the given preferred timezone.
 * @param {(DateTime|null)} date
 * @param {string} tz name of timezone
 * @returns {(DateTime|null)} the time in the timezone.
 */
export const dateToTimeInTimezone = (date, tz) => {
  if (!date) { return null; }

  const timezone = tz || APIConfig().DEFAULT_TIMEZONE;

  return DateTime.fromObject(
    { year: date.year, month: date.month, day: date.day },
    { zone: timezone },
  );
};

/**
 * Returns the timezone to use, based on following priority order:
 * 1. Explicit from the URL
 * 2. Implicit from the property (if applicable)
 * 3. Implicit from config
 * 4. Implicit from system
 * @param {string} timezone
 * @returns {string} The timezone to use.
 */
export const getTimezone = (timezone = '') => {
  const searchParams = new URLSearchParams(window.location.search);
  const requestedTimezone = searchParams.get('tz');
  if (requestedTimezone) {
    return requestedTimezone;
  }

  return timezone || APIConfig().DEFAULT_TIMEZONE || TIME_ZONE_SYSTEM;
};

/**
 * For a given date range a finish date is represented as midnight of the day in our date range
 * object, however as a time range it is considered at 24:00:00 that is midnight the following day.
 * A day is a 24 hours interval (not withstanding daylight saving and/or leap seconds). Hence the
 * addition of one day.
 * @param {object} dateRange
 * @param {DateTime} dateRange.start
 * @param {DateTime} dateRange.finish
 * @param {string} tz
 * @returns {object} - time range.
 */
export const dateRangeToTimeRange = (dateRange, tz) => {
  if (!dateRange) return null;

  const timezone = tz || APIConfig().DEFAULT_TIMEZONE;
  const { start: startDate, finish: finishDate } = dateRange;

  const start = dateToTimeInTimezone(startDate, timezone);
  const finish = dateToTimeInTimezone(finishDate, timezone)?.plus({ days: 1 });

  return { start, finish };
};

/**
 * Check whether the timespan has changed.
 * Compares between the proposed timespan and the current timespan.
 * If the dates are the same, logs a message and returns `false`; otherwise, returns `true`.
 * @param {DateTime} inputStart - The proposed start time of the timespan.
 * @param {DateTime} inputFinish - The proposed finish time of the timespan.
 * @param {object} currentTimespan - The current timespan to compare against.
 * @returns {boolean} - returns true if the timespan is valid and false if not.
 */
export const timespanChanged = (inputStart, inputFinish, currentTimespan) => {
  // Validate inputs
  if (!inputStart || !inputFinish || !inputStart.isValid || !inputFinish.isValid) {
    console.error('Invalid date inputs to handleTimespanUpdate:', { inputStart, inputFinish });
    return false;
  }

  // Check if this is the same as the current datespan to avoid unnecessary updates
  const isSameStart = inputStart.toMillis() === currentTimespan.start.toMillis();
  const isSameFinish = inputFinish.toMillis() === currentTimespan.finish.toMillis();

  if (isSameStart && isSameFinish) {
    console.log('Timespan update skipped - dates are identical');
    return false;
  }
  return true;
};

/**
 * Converts a variety of time representations to epoch milliseconds.
 * @param {number|Date|{toMillis: function(): number}|null|undefined} v - The value to convert.
 * @returns {number|null} Epoch milliseconds or null if invalid.
 */
export const toMillis = (v) => {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'object' && v !== null && 'toMillis' in v && typeof v.toMillis === 'function') {
    const n = v.toMillis();
    return Number.isFinite(n) ? n : null;
  }
  return null;
};
